import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseDate } from '@internationalized/date';
import { ChevronDown } from 'lucide-react';
import programApi from '@src/features/programs/services/program-api';
import MultiSelectFilter from '@src/features/dashboard/components/multiselect-filter';
import {
  DatesOptions,
  DefaultDateVlue,
} from '@src/features/dashboard/constants';
import { localTodayString } from '@src/features/dashboard/helper/date-helper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { Checkbox } from '@src/design-system/checkbox';
import { cn } from '@src/design-system/helpers';
import {
  TiclickDatePicker,
  TiclickDatePickerCalendarPopover,
  TiclickDatePickerCalendarPopoverTrigger,
} from '@src/design-system/ticlick-date-picker';
import { FilterSearch } from 'iconsax-react';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { PageContainer } from '@src/shared/components/page-container';
import { PageTitle } from '@src/shared/components/page-title';
import { FailedReminders } from '../components/reminders-dashboard/failed-reminders';
import { ReminderOverviewStats } from '../components/reminders-dashboard/overview-widgets/reminder-overview-stats';
import { ReminderOverviewPerformance } from '../components/reminders-dashboard/overview-widgets/reminder-overview-performance';
import { ReminderOverviewChannelCards } from '../components/reminders-dashboard/overview-widgets/reminder-overview-channel-cards';

export const RemindersOverviewPage = () => {
  const [params, setParams] = useSearchParams();
  const [localDateFilter, setLocalDateFilter] = useState<string>(
    params.get('date') || DefaultDateVlue
  );
  const selectedChannels = useMemo(
    () => params.getAll('channel'),
    [params.toString()]
  );
  const isAllChannelsSelected = selectedChannels.length === 0;

  useEffect(() => {
    if (!params.get('date')) {
      const newParams = new URLSearchParams(params);
      newParams.set('date', DefaultDateVlue);
      setParams(newParams, { replace: true });
    }
  }, [params, setParams]);

  const today = useMemo(() => parseDate(localTodayString()), []);

  const handleDateChange = (value: string) => {
    setLocalDateFilter(value);
    const newParams = new URLSearchParams(params);
    newParams.set('date', value);
    setParams(newParams);
  };

  const setChannelParams = (channels: string[]) => {
    const newParams = new URLSearchParams(params);
    newParams.delete('channel');
    channels.forEach((c) => newParams.append('channel', c));
    setParams(newParams);
  };

  return (
    <PageContainer>
      <PageTitle titles={['Reminders', 'Overview']} />
      <div className='pb-8 pt-3 space-y-4'>
        <div className='flex gap-x-2 '>
          <Popover>
            <PopoverTrigger
              className={cn(
                'w-fit max-w-[260px] border-primary-200 px-3  border bg-white rounded-[12px]',
                'hover:bg-primary-50 cursor-pointer text-left'
              )}
            >
              <div className='flex items-center gap-2'>
                <FilterSearch
                  size={24}
                  variant='TwoTone'
                  className='text-gray-500 shrink-0'
                />
                <span className='text-gray-800 font-medium text-sm truncate'>
                  {isAllChannelsSelected
                    ? 'All channels'
                    : `${selectedChannels.length} channel${selectedChannels.length === 1 ? '' : 's'}`}
                </span>
                <ChevronDown
                  className='text-gray-1000 shrink-0'
                  size={18}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className='w-[260px] p-2 z-50 bg-white border border-primary-200 rounded-lg shadow-md'
              align='start'
            >
              <div className='flex flex-col gap-1'>
                <label className='flex items-center gap-2 px-2 py-2 rounded-md hover:bg-primary-50 cursor-pointer'>
                  <Checkbox
                    checked={isAllChannelsSelected}
                    onCheckedChange={(checked) => {
                      if (checked === true) setChannelParams([]);
                    }}
                    className='border border-primary-200'
                  />
                  <span className='text-sm text-gray-1000 font-medium'>
                    All channels
                  </span>
                </label>
                <div className='border-t border-primary-100 my-1' />
                {Object.values(REMINDER_CHANNELS).map((ch) => (
                  <label
                    key={ch}
                    className='flex items-center gap-2 px-2 py-2 rounded-md hover:bg-primary-50 cursor-pointer'
                  >
                    <Checkbox
                      checked={selectedChannels.includes(ch)}
                      onCheckedChange={(checked) => {
                        if (checked === true) {
                          if (isAllChannelsSelected) {
                            setChannelParams([ch]);
                          } else if (!selectedChannels.includes(ch)) {
                            setChannelParams([...selectedChannels, ch]);
                          }
                        } else if (
                          checked === false &&
                          !isAllChannelsSelected
                        ) {
                          setChannelParams(
                            selectedChannels.filter((c) => c !== ch)
                          );
                        }
                      }}
                      className='border border-primary-200'
                    />
                    <span className='text-sm text-gray-1000'>
                      {formatTextForDisplay(ch)}
                    </span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Select
            value={localDateFilter}
            onValueChange={handleDateChange}
          >
            <SelectTrigger className='w-fit hover:bg-primary-50 cursor-pointer max-w-[260px] rounded-[12px] border-primary-200 py-5 px-3 border bg-white data-[placeholder]:text-gray-1000 data-[placeholder]:font-medium'>
              {localDateFilter.includes('/') ? (
                <div className='flex items-center gap-2'>
                  <FilterSearch
                    size={24}
                    variant='TwoTone'
                    className='text-gray-500'
                  />
                  <span className='text-gray-800 font-medium text-sm truncate'>
                    {localDateFilter}
                  </span>
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  <FilterSearch
                    size={24}
                    variant='TwoTone'
                    className='text-gray-500'
                  />
                  <SelectValue
                    className='text-gray-800 font-medium text-sm truncate'
                    placeholder='Last 30 days'
                  />
                </div>
              )}
            </SelectTrigger>
            <SelectContent>
              {DatesOptions.map((opt) => (
                <SelectItem
                  className='border-b border-b-primary-200 rounded-none'
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </SelectItem>
              ))}
              <TiclickDatePicker
                maxValue={today}
                onChange={(value) => {
                  const start = value?.start?.toString();
                  const end = value?.end?.toString();
                  if (!start || !end) return;
                  handleDateChange(`${start} / ${end}`);
                }}
                mode='range'
              >
                <TiclickDatePickerCalendarPopoverTrigger className='w-full flex cursor-default hover:bg-primary-100 select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-primary-200 focus:text-gray-800 data-[disabled]:pointer-events-none data-[disabled]:opacity-50'>
                  Date Range
                </TiclickDatePickerCalendarPopoverTrigger>
                <TiclickDatePickerCalendarPopover sideOffset={-10} />
              </TiclickDatePicker>
            </SelectContent>
          </Select>

          <MultiSelectFilter
            fetchMethod={programApi.useFetchInfinite}
            filterName='programs[]'
            placeHolder='Program Name'
            label='Programs'
            className='rounded-[12px]'
          />
        </div>

        <ReminderOverviewStats />

        <ReminderOverviewChannelCards />
        <FailedReminders />
        <ReminderOverviewPerformance />

        {/* <UpcomingReminders /> */}
      </div>
    </PageContainer>
  );
};
